import React, {
  useRef,
  useMemo,
  useEffect,
  useState,
  useCallback,
} from "react";
import { Modal, Button, message } from "antd";
import type { UploadFile } from "antd";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import DynamicForm from "@/components/DynamicForm";
import ProcessTimeline from "@/components/ProcessTimeline";
import type { FormInstance } from "antd";
import type { ProcessTimelineItem } from "@/components/ProcessTimeline";
import { useModalStore } from "@/stores/modalStore";
import { getAdmissionApplicationFormItems } from "./config/formConfig";
import {
  getAdmissionApplicationStep2FormItems,
  getStep2ModalFooter,
  ACCOUNT_NUMBER_MAPPING,
  transformStep2FormDataWithNames,
} from "./config/formConfig2";
import type {
  SupplierAdmissionFormData,
  SupplierAdmissionCompleteFormData,
  SupplierAdmissionModalProps,
} from "./config/types";
import { fillApplicantInfo } from "@/pages/supplier/SupplierProfile/utils/fillFormFields";
import {
  transformFormDataToRegistrantList,
  mergeFormDataWithRegistrants,
} from "./utils/transformFormData";
import {
  generateProcessCode,
  getHistoryByInsId,
  checkBatchOpenBankNumber,
  getAccessEvaluationInfoById,
} from "@/apis/supplier";
import { passFlow } from "@/apis/flow.api";
import styles from "./index.module.less";

// ==================== 常量配置 ====================

const FORM_CONFIG = {
  labelCol: { span: 6 },
  wrapperCol: { span: 18 },
  layout: "horizontal" as const,
  colon: false,
};

/**
 * 表单步骤枚举
 */
enum FormStep {
  STEP1 = 1,
  STEP2 = 2,
}

// ==================== 辅助函数 ====================

/**
 * 文件类型标签到数字的映射
 */
const LABEL_TO_TYPE_MAP: Record<string, number> = {
  营业执照: 0,
  银行信息: 1,
  收款授权信: 2,
  开户证明: 3,
  其他: 4,
};

/**
 * 将标签字符串规范化
 */
const normalizeLabel = (label: string): string => {
  return label.trim().toLowerCase();
};

/**
 * 创建规范化的反向映射表
 */
const createNormalizedLabelMap = (): Record<string, number> => {
  const map: Record<string, number> = {};
  Object.entries(LABEL_TO_TYPE_MAP).forEach(([label, type]) => {
    map[normalizeLabel(label)] = type;
  });
  return map;
};

const NORMALIZED_LABEL_TO_TYPE_MAP = createNormalizedLabelMap();

/**
 * 文件类型映射关系
 */
const FILE_TYPE_MAP: Record<number, { label: string; fieldName: string }> = {
  0: { label: "营业执照", fieldName: "businessLicense" },
  1: { label: "银行信息", fieldName: "bankDocs" },
  2: { label: "收款授权信", fieldName: "paymentAuthorizationLetter" },
  3: { label: "开户证明", fieldName: "bankOpeningProof" },
  4: { label: "其他", fieldName: "otherDocs" },
};

/**
 * 解析逗号隔开的文件URL字符串
 */
const parseFileUrls = (fileUrlString?: string) => {
  if (!fileUrlString || typeof fileUrlString !== "string") {
    return [];
  }

  return fileUrlString
    .split(",")
    .map((url, index) => {
      const trimmedUrl = url.trim();
      const fileName = trimmedUrl.includes("/")
        ? trimmedUrl.split("/").pop() || `文件${index + 1}`
        : trimmedUrl || `文件${index + 1}`;

      return {
        name: fileName,
        url: trimmedUrl,
        index,
      };
    })
    .filter((item) => item.url);
};

/**
 * 将 fileType 转换为数字类型
 * 支持数字和字符串两种格式
 */
const normalizeFileType = (fileType?: number | string): number => {
  // 如果是数字，直接返回
  if (typeof fileType === "number") {
    return fileType;
  }

  // 如果是字符串，进行转换
  if (typeof fileType === "string") {
    // 先尝试精确匹配
    const exactMatch = LABEL_TO_TYPE_MAP[fileType];
    if (exactMatch !== undefined) {
      return exactMatch;
    }

    // 再尝试规范化后匹配
    const normalizedLabel = normalizeLabel(fileType);
    const normalizedMatch = NORMALIZED_LABEL_TO_TYPE_MAP[normalizedLabel];
    if (normalizedMatch !== undefined) {
      return normalizedMatch;
    }

    // 如果都没找到，尝试 parseInt
    const parsedType = parseInt(fileType, 10);
    if (!isNaN(parsedType) && FILE_TYPE_MAP[parsedType]) {
      return parsedType;
    }
    return 4; // 默认为"其他"
  }

  // 默认为"其他"
  return 4;
};

/**
 * 转换营业有效期数据格式
 * RangePicker 返回 [startDate, endDate] 数组，需要转换为字符串格式
 * 格式：如果有两个日期，转换为 "YYYY-MM-DD ~ YYYY-MM-DD"
 * 如果只有一个日期或为空，保持原值
 *
 * @param formValues 表单数据
 * @returns 转换后的表单数据
 */
const transformBusinessValidityData = (formValues: any) => {
  const result = { ...formValues };

  if (result.businessValidity && Array.isArray(result.businessValidity)) {
    const [startDate, endDate] = result.businessValidity;

    // 如果两个日期都存在，转换为字符串格式
    if (startDate && endDate) {
      const startStr = startDate.format
        ? startDate.format("YYYY-MM-DD")
        : startDate;
      const endStr = endDate.format ? endDate.format("YYYY-MM-DD") : endDate;
      result.businessValidity = `${startStr} ~ ${endStr}`;
    } else if (startDate) {
      // 只有开始日期
      result.businessValidity = startDate.format
        ? startDate.format("YYYY-MM-DD")
        : startDate;
    } else {
      // 清空无效数据
      result.businessValidity = undefined;
    }
  }

  return result;
};

/**
 * 反向转换营业有效期数据格式
 * 将字符串格式 "YYYY-MM-DD ~ YYYY-MM-DD" 转换回 RangePicker 需要的数组格式
 * 用于初始化表单时使用
 *
 * @param formValues 表单数据
 * @returns 转换后的表单数据
 */
const reverseTransformBusinessValidityData = (formValues: any) => {
  try {
    const result = { ...formValues };

    // 只处理businessValidity字段，其他字段保持不变
    if (result.businessValidity) {
      const validity = result.businessValidity;

      // 如果已经是数组格式（dayjs对象数组），直接返回
      if (Array.isArray(validity)) {
        return result;
      }

      // 如果是字符串格式，进行转换
      if (typeof validity === "string") {
        // 检查是否是 "YYYY-MM-DD ~ YYYY-MM-DD" 格式
        if (validity.includes("~")) {
          const parts = validity.split("~");
          if (parts.length === 2) {
            const startStr = parts[0].trim();
            const endStr = parts[1].trim();
            const startDate = dayjs(startStr);
            const endDate = dayjs(endStr);

            // 验证日期是否有效
            if (startDate.isValid() && endDate.isValid()) {
              result.businessValidity = [startDate, endDate];
            } else {
            }
          }
        } else {
          // 单个日期
          const date = dayjs(validity);
          if (date.isValid()) {
            result.businessValidity = [date];
          } else {
          }
        }
      }
    }

    return result;
  } catch (error) {
    // 如果出错，返回原始数据，不修改
    return formValues;
  }
};

/**
 * 将 profileList 转换为表单所需的字段格式
 * 例如：fileType=0 的文件会被转换为 businessLicense 字段
 *
 * 注意：此函数与 formConfig.tsx 的 generateProfileFileFormItems 中的逻辑必须保持同步
 */
const transformProfileListToFormFields = (recordData: any) => {
  const formData = { ...recordData };

  if (!recordData?.profileList || recordData.profileList.length === 0) {
    return formData;
  }

  const fileTypeCount: Record<number, number> = {};

  recordData.profileList.forEach((profile: any) => {
    // 使用改进的 normalizeFileType 函数转换 fileType
    const fileType = normalizeFileType(profile.fileType);
    const fileTypeConfig = FILE_TYPE_MAP[fileType];

    if (!fileTypeConfig) {
      // 已经是 4 了，无需再处理
    }

    // 追踪该文件类型出现的次数，确保字段名唯一
    fileTypeCount[fileType] = (fileTypeCount[fileType] ?? 0) + 1;

    const { fieldName } = FILE_TYPE_MAP[fileType];
    const uniqueFieldName =
      fileTypeCount[fileType] > 1
        ? `${fieldName}_${fileTypeCount[fileType]}`
        : fieldName;

    const files = parseFileUrls(profile.fileUrl);

    // 转换为 Upload 组件需要的格式
    const uploadFiles: UploadFile[] = files.map((file) => ({
      uid: `${file.url}`,
      name: file.name,
      status: "done",
      url: file.url,
      fileId: profile.id, // 保留原文件的ID（从profileList中获取）
      originFileObj: undefined,
    }));

    formData[uniqueFieldName] = uploadFiles;
  });
  return formData;
};

// ==================== 渲染函数 ====================

/**
 * 获取第一步Modal标题
 */
const getModalTitle = (processCode: string = "", t: any) => {
  return (
    <div className={styles.titleContainer}>
      <span>{t("供应商准入申请表")}</span>
      <div className={styles.titleDivider} />
      <span className={styles.titleProcessCode}>
        {t("单号")}：{processCode || t("加载中")}
      </span>
    </div>
  );
};

/**
 * 获取第一步Modal底部按钮（只读模式 - 第一步）
 */
const getReadOnlyStep1ModalFooter = (
  handleNext: () => void,
  handleClose: () => void,
  t: any,
) => (
  <div className={styles.modalFooterContainer}>
    <Button onClick={handleClose}>{t("关闭")}</Button>
    <Button type="primary" onClick={handleNext}>
      {t("下一步")}
    </Button>
  </div>
);

/**
 * 获取第二步Modal底部按钮（只读模式）
 */
const getReadOnlyStep2ModalFooter = (
  handlePrev: () => void,
  handleClose: () => void,
  t: any,
) => (
  <div className={styles.modalFooterContainer}>
    <Button onClick={handlePrev}>{t("上一步")}</Button>
    <Button onClick={handleClose}>{t("关闭")}</Button>
  </div>
);

/**
 * 获取第一步Modal底部按钮
 */
const getModalFooter = (handleNext: () => void, t: any) => (
  <div className={styles.modalFooterContainer}>
    <Button type="primary" onClick={handleNext}>
      {t("下一步")}
    </Button>
  </div>
);

// ==================== 主组件 ====================

const AdmissionApplicationModal: React.FC<SupplierAdmissionModalProps> = ({
  visible,
  onClose,
  onSubmit,
  recordData,
  readOnly = false,
  isApplyChangeMode = false,
  isResubmitMode = false,
  resubmitStep1DelayMs = 0,
  isFromWorkbench = false,
}) => {
  const { t } = useTranslation("supplierProfile");
  const formRef = useRef<FormInstance>(null);
  const [currentStep, setCurrentStep] = useState<FormStep>(FormStep.STEP1);
  const [processCode, setProcessCode] = useState<string>("");
  // 保存第一步的数据
  const [step1Data, setStep1Data] = useState<SupplierAdmissionFormData | null>(
    null,
  );
  // 流程动态状态
  const [timelineData, setTimelineData] = useState<ProcessTimelineItem[]>([]);
  // 保存 SAP 验证失败的开户行行号，用于在表格中标红
  const [errorBankNumbers, setErrorBankNumbers] = useState<string[]>([]);

  // ==================== 表单配置生成 ====================

  const formItems = useMemo(
    () =>
      getAdmissionApplicationFormItems(
        recordData?.registrantList,
        recordData?.bankInfoList,
        recordData?.profileList,
        formRef,
        readOnly, // 传递 readOnly 标志
        isApplyChangeMode, // 传递 isApplyChangeMode 标志
        errorBankNumbers, // 传递 SAP 验证失败的开户行行号
        t, // 传递翻译函数
      ),
    [
      recordData?.registrantList,
      recordData?.bankInfoList,
      recordData?.profileList,
      readOnly,
      isApplyChangeMode,
      errorBankNumbers,
      t,
    ],
  );

  const { openModal } = useModalStore();

  // ==================== 事件处理 ====================

  /**
   * 打开评估表单弹窗
   * 先调取接口获取评估数据，然后打开弹窗
   */
  const handleViewEvaluation = useCallback(async () => {
    if (!recordData?.id) {
      message.error(t("缺少必要的数据无法打开评估表单"));
      return;
    }

    setEvaluationLoading(true);
    try {
      // 调用接口获取评估详情
      const response = await getAccessEvaluationInfoById(recordData.id);

      if (response.data?.code === 200 && response.data?.data) {
        const evaluationData = response.data.data;

        // 打开评估表单弹窗，传递获取到的数据
        openModal("ZHUN_RU_PING_GU", {
          recordId: recordData.id,
          mode: "view",
          viewStatus: true,
          isReadOnly: true,
          evaluationData: evaluationData, // 传递评估数据
        });
      } else {
        message.error(response.data?.message || t("获取评估详情失败"));
      }
    } catch (error) {
      message.error(t("获取评估详情失败，请重试"));
    } finally {
      setEvaluationLoading(false);
    }
  }, [recordData?.id, openModal, t]);

  const step2FormItems = useMemo(
    () =>
      getAdmissionApplicationStep2FormItems(
        isFromWorkbench,
        handleViewEvaluation,
        t,
      ),
    [isFromWorkbench, handleViewEvaluation, t],
  );

  const modalTitle = useMemo(
    () => getModalTitle(processCode, t),
    [processCode, t],
  );

  // 获取单号
  useEffect(() => {
    if (visible && !processCode) {
      // 来自工作台：直接使用 recordData 中的单号，不调用生成接口
      if (isFromWorkbench) {
        // 工作台模式下，等待 recordData 加载完成
        if (recordData?.processCode) {
          setProcessCode(recordData.processCode);
        }
        // 如果 recordData 还没加载，不执行其他逻辑
        return;
      }

      if (isApplyChangeMode) {
        // 申请变更模式：需要生成新的单号（不使用 recordData 中的单号）
        const fetchProcessCode = async () => {
          try {
            const response = await generateProcessCode("ZHUN_RU_SHEN_QING");
            // 根据 API 响应结构获取单号（在 data 字段中）
            let code = response?.data.data;
            if (typeof code === "string") {
              // 移除"单号："前缀和"加载中..."等文本，只保留实际的单号
              code = code.replace(/^[^A-Z0-9]*/, "").trim();
              if (code) {
                setProcessCode(code);
              }
            }
          } catch (error) {
            message.error(t("获取单号失败，请重新打开"));
          }
        };

        fetchProcessCode();
      } else if (isResubmitMode) {
        // 重新提交模式：需要生成新的单号
        const fetchProcessCode = async () => {
          try {
            const response = await generateProcessCode("ZHUN_RU_SHEN_QING");
            // 根据 API 响应结构获取单号（在 data 字段中）
            let code = response?.data.data;
            if (typeof code === "string") {
              // 移除"单号："前缀和"加载中..."等文本，只保留实际的单号
              code = code.replace(/^[^A-Z0-9]*/, "").trim();
              if (code) {
                setProcessCode(code);
              }
            }
          } catch (error) {
            message.error(t("获取单号失败，请重新打开"));
          }
        };

        fetchProcessCode();
      } else if (recordData?.processCode) {
        // 编辑状态：从 recordData 中获取 processCode
        setProcessCode(recordData.processCode);
      } else {
        // 新增状态：调用接口生成单号
        const fetchProcessCode = async () => {
          try {
            const response = await generateProcessCode("ZHUN_RU_SHEN_QING");
            // 根据 API 响应结构获取单号（在 data 字段中）
            let code = response?.data.data;
            if (typeof code === "string") {
              // 移除"单号："前缀和"加载中..."等文本，只保留实际的单号
              code = code.replace(/^[^A-Z0-9]*/, "").trim();
              if (code) {
                setProcessCode(code);
              }
            }
          } catch (error) {
            message.error(t("获取单号失败，请重新打开"));
          }
        };

        fetchProcessCode();
      }
    }
  }, [
    visible,
    recordData,
    isResubmitMode,
    isApplyChangeMode,
    processCode,
    isFromWorkbench,
    t,
  ]);

  // 保存转换后的表单数据用于 DynamicForm
  const [formInitialValue, setFormInitialValue] = useState<any>(null);
  // 保存第二步的初始值
  const [step2InitialValue, setStep2InitialValue] = useState<any>(null);

  // 处理空值显示为 "-"
  const handleEmptyValues = (data: any) => {
    return Object.entries(data).reduce((acc, [key, value]) => {
      acc[key] = !value && typeof value !== "number" ? "-" : value;
      return acc;
    }, {} as any);
  };

  // 当弹框打开或recordData改变时，初始化表单数据
  React.useEffect(() => {
    if (visible && recordData) {
      // 获取历史记录（在查看模式或重新提交模式下调取）
      if (recordData?.insId && (readOnly || isResubmitMode)) {
        getHistoryByInsId(recordData.insId)
          .then((historyResponse) => {
            // 将历史记录转换为 ProcessTimelineItem 格式
            if (
              historyResponse?.data?.code === 200 &&
              Array.isArray(historyResponse?.data?.data)
            ) {
              const timeline = historyResponse.data.data.map((item: any) => ({
                approver: item.approver,
                flowStatus: item.flowStatus,
                postName: item.postName,
                createTime: item.createTime,
                message: item.message,
                avatarUrl:
                  item.avatarUrl ||
                  "https://gw.alipayobjects.com/zos/antfincdn/efFD%24IOql2/weixintupian_20170331104822.jpg",
              }));
              setTimelineData(timeline);
            }
          })
          .catch((historyError) => {});
      } else {
        if (!recordData?.insId) {
        } else if (!readOnly && !isResubmitMode) {
        }
      }

      // 转换 profileList 为表单需要的字段格式
      const formDataWithFiles = transformProfileListToFormFields(recordData);

      const formDataWithConvertedValidity =
        reverseTransformBusinessValidityData(formDataWithFiles);

      // 处理空值显示为 "-"
      const formDataWithEmptyHandled = handleEmptyValues(
        formDataWithConvertedValidity,
      );

      // 使用 formValue 属性而不是 setFieldsValue，这样可以确保正确初始化
      setFormInitialValue(formDataWithEmptyHandled);

      // 提取第二步的初始值（从 recordData 中提取相关字段）
      // recordData 已经由 transformSupplierDataToFormData 转换，包含所有第二步字段
      const step2Data = {
        supplierCode: recordData.supplierCode,
        factoryValue: recordData.factoryValue,
        materialCategoryValue: recordData.materialCategoryValue,
        materialTwoCategoryName: recordData.materialTwoCategoryName,
        materialTwoCategoryValue: recordData.materialTwoCategoryValue,
        materialPurposeName: recordData.materialPurposeName,
        materialPurposeValue: recordData.materialPurposeValue,
        isGmpValue: recordData.isGmpValue,
        purchaseTypeValue: recordData.purchaseTypeValue,
        supplierLevelValue: recordData.supplierLevelValue,
        purchaseGroupValue: recordData.purchaseGroupValue,
        currencyValue: recordData.currencyValue,
        paymentMethodValue: recordData.paymentMethodValue,
        paymentTermsValue: recordData.paymentTermsValue,
        internationalTradeTermsValue: recordData.internationalTradeTermsValue,
        shoppingName: recordData.shoppingName,
        accountGroupName: recordData.accountGroupName,
        accountGroupValue: recordData.accountGroupValue,
        accountCode: recordData.accountCode,
        oaAddress: recordData.oaAddress,
        oaAddressCode: recordData.oaAddressCode,
        piInvoiceParty: recordData.piInvoiceParty,
        piInvoicePartyCode: recordData.piInvoicePartyCode,
      };
      setStep2InitialValue(step2Data);
    } else if (!visible) {
      formRef.current?.resetFields();
      setCurrentStep(FormStep.STEP1);
      setProcessCode("");
      setFormInitialValue(null);
      setStep2InitialValue(null);
      setTimelineData([]);
    }
  }, [visible, recordData, readOnly, isResubmitMode]);

  // 监听 Modal 打开状态，当 Modal 打开时填充申请人信息
  useEffect(() => {
    if (visible && currentStep === FormStep.STEP1 && formInitialValue) {
      // 使用 setTimeout 确保表单已完全初始化
      const timer = setTimeout(() => {
        const success = fillApplicantInfo(formRef);
        if (!success) {
          message.warning(t("无法获取用户信息，请重新登录"));
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [visible, currentStep, formInitialValue, t]);

  // 获取动态表单配置（根据当前步骤调整）
  const dynamicFormConfig = useMemo(() => {
    if (currentStep === FormStep.STEP2) {
      return {
        ...FORM_CONFIG,
        onValuesChange: (changedValues: any, allValues: any) => {
          // 监听科目小组变化，自动填充科目编号（只在非只读模式下）
          if (!readOnly && changedValues.accountGroupValue && formRef.current) {
            const accountCode =
              ACCOUNT_NUMBER_MAPPING[changedValues.accountGroupValue]?.[0];
            if (accountCode) {
              formRef.current.setFieldValue("accountCode", accountCode);
            }
          }
        },
      };
    }
    // STEP1 也需要 onValuesChange，以确保文件上传时能触发组件重新渲染
    return {
      ...FORM_CONFIG,
      onValuesChange: (changedValues: any, allValues: any) => {
        // 触发重新渲染以更新文件列表
      },
    };
  }, [currentStep, readOnly]);

  /**
   * 检查银行信息是否有输入
   */
  const checkBankInfoValidation = async (
    bankInfoList: any[],
  ): Promise<{ valid: boolean; errorBankNumbers?: string[] }> => {
    // 检查是否有银行信息
    if (!bankInfoList || bankInfoList.length === 0) {
      message.error(t("请至少添加一条银行信息"));
      return { valid: false };
    }

    // 检查每条银行信息是否都已完整输入（必填字段）
    const hasEmptyBank = bankInfoList.some((bank: any) => {
      return (
        !bank.accountType ||
        !bank.country ||
        !bank.openBankName ||
        !bank.openBankNumber ||
        !bank.account ||
        !bank.accountHolder
      );
    });

    if (hasEmptyBank) {
      message.error(t("请完整填写所有银行信息的必填字段"));
      return { valid: false };
    }

    // 调用 SAP 接口检测开户行行号
    try {
      const openBankNumbers = bankInfoList.map(
        (bank: any) => bank.openBankNumber,
      );
      const response = await checkBatchOpenBankNumber(openBankNumbers);

      if (response?.data?.code === 200 && response?.data?.data) {
        const notFoundBankNumbers = response.data.data;

        // 如果有开户行行号在 SAP 中不存在，显示错误提示
        if (
          Array.isArray(notFoundBankNumbers) &&
          notFoundBankNumbers.length > 0
        ) {
          // 生成详细的错误提示，包含具体的开户行行号
          const errorBankNumbersStr = notFoundBankNumbers.join("、");
          message.error(
            `${t("抱歉SAP没有这些标红的开户行行号")}：${errorBankNumbersStr}，${t("无法进行下一步")}`,
          );
          return { valid: false, errorBankNumbers: notFoundBankNumbers };
        }
      }

      return { valid: true };
    } catch (error) {
      message.error(t("检测开户行行号失敗，请重試"));
      return { valid: false };
    }
  };

  /**
   * 处理第一步下一步按钮点击
   */
  const handleStep1Next = async () => {
    try {
      // 只读模式下不需要验证，直接切换到第二步
      if (readOnly) {
        // 如果设置了延迟，则使用延迟
        if (resubmitStep1DelayMs > 0) {
          setTimeout(() => {
            setCurrentStep(FormStep.STEP2);
          }, resubmitStep1DelayMs);
        } else {
          setCurrentStep(FormStep.STEP2);
        }
        return;
      }

      // 重新提交模式和编辑模式需要验证表单
      let values: any;
      try {
        values = await formRef.current?.validateFields();
      } catch (validateError) {
        return;
      }

      if (values) {
        // 转换营业有效期数据格式（RangePicker -> 字符串）
        const transformedValues = transformBusinessValidityData(values);

        // 先检查银行信息是否存在
        if (
          !transformedValues.bankInfoList ||
          transformedValues.bankInfoList.length === 0
        ) {
          message.error(t("请至少添加一条银行信息"));
          return;
        }

        // 检查银行信息是否已输入并通过 SAP 验证
        const validationResult = await checkBankInfoValidation(
          transformedValues.bankInfoList,
        );
        if (!validationResult.valid) {
          // 如果有验证失败的开户行行号，记录下来用于在表格中标红
          if (validationResult.errorBankNumbers) {
            setErrorBankNumbers(validationResult.errorBankNumbers);
            // 同时更新表单中对应的 bankInfoList，将对应的 openBankInSap 设置为 0
            const updatedBankList = transformedValues.bankInfoList.map(
              (bank: any) => {
                if (
                  validationResult.errorBankNumbers?.includes(
                    bank.openBankNumber,
                  )
                ) {
                  return { ...bank, openBankInSap: 0 };
                }
                return bank;
              },
            );
            formRef.current?.setFieldsValue({ bankInfoList: updatedBankList });
          }
          return;
        }

        // 转换注册人信息：将平层的 firstName_1, firstName_2 等转换为 registrantList 数组
        const registrantListLength = recordData?.registrantList?.length || 1;
        const registrantList = transformFormDataToRegistrantList(
          values,
          registrantListLength,
          recordData?.registrantList, // 传递原始 registrantList，以保留 id 等字段
        );

        // 合并数据：移除平层的注册人字段，添加 registrantList 数组
        const cleanedValues = mergeFormDataWithRegistrants(
          values,
          registrantList,
          registrantListLength,
        );

        // 处理银行信息：只在 id 存在时才保留，新增的银行不包含 id
        if (
          cleanedValues.bankInfoList &&
          Array.isArray(cleanedValues.bankInfoList)
        ) {
          cleanedValues.bankInfoList = cleanedValues.bankInfoList.map(
            (bank: any) => {
              // 如果 id 存在（来自后端的已有数据），保留它
              // 如果 id 不存在（新增的数据），删除 id 字段
              if (bank.id !== undefined && bank.id !== null) {
                return bank;
              } else {
                const { id, ...bankWithoutId } = bank;
                return bankWithoutId;
              }
            },
          );
        }

        // 从 profileList 中提取 id 值作为 fileIds（已有的文件）
        const existingFileIds =
          recordData?.profileList?.map((profile: any) => profile.id) || [];

        // 从表单的文件字段中提取新上传文件的 fileId
        // 文件字段有：businessLicense, bankDocs, paymentAuthorizationLetter, bankOpeningProof, otherDocs
        const FILE_FIELD_NAMES = [
          "businessLicense",
          "bankDocs",
          "paymentAuthorizationLetter",
          "bankOpeningProof",
          "otherDocs",
        ];

        const newFileIds = FILE_FIELD_NAMES.flatMap((fieldName) => {
          const fileList = values[fieldName];
          if (!Array.isArray(fileList)) return [];
          return fileList
            .filter((file: any) => file.fileId) // 只收集有 fileId 的文件（新上传的）
            .map((file: any) => {
              // 如果 fileId 是数组，取第一个元素；否则直接返回
              return Array.isArray(file.fileId) ? file.fileId[0] : file.fileId;
            });
        });

        // 合并已有的和新上传的文件 ID
        const fileIds = [...existingFileIds, ...newFileIds];
        // 保存第一步数据，并添加供应商ID、流程单号和文件IDs
        // 注：supplierUserId 来自 recordData（供应商详情接口返回），不是表单字段
        // 优先使用 supplierUserId，如果没有则使用 id（新增模式下 id 就是 supplierUserId）
        const step1DataWithIdentifiers = {
          ...cleanedValues,
          supplierUserId: recordData?.supplierUserId || recordData?.id,
          processCode: processCode,
          fileIds: fileIds,
        } as SupplierAdmissionFormData;

        setStep1Data(step1DataWithIdentifiers as any);

        // 提取第二步的初始值（优先使用第一步中已修改的字段值）
        // 对于不在第一步表单中的字段，使用 recordData 中的原始值
        const step2Data = {
          supplierCode: cleanedValues.supplierCode || recordData?.supplierCode,
          factoryValue: cleanedValues.factoryValue || recordData?.factoryValue,
          materialCategoryValue:
            cleanedValues.materialCategoryValue ||
            recordData?.materialCategoryValue,
          materialTwoCategoryName:
            cleanedValues.materialTwoCategoryName ||
            recordData?.materialTwoCategoryName,
          materialTwoCategoryValue:
            cleanedValues.materialTwoCategoryValue ||
            recordData?.materialTwoCategoryValue,
          materialPurposeName:
            cleanedValues.materialPurposeName ||
            recordData?.materialPurposeName,
          materialPurposeValue:
            cleanedValues.materialPurposeValue ||
            recordData?.materialPurposeValue,
          isGmpValue: cleanedValues.isGmpValue || recordData?.isGmpValue,
          purchaseTypeValue:
            cleanedValues.purchaseTypeValue || recordData?.purchaseTypeValue,
          supplierLevelValue:
            cleanedValues.supplierLevelValue || recordData?.supplierLevelValue,
          purchaseGroupValue:
            cleanedValues.purchaseGroupValue || recordData?.purchaseGroupValue,
          currencyValue:
            cleanedValues.currencyValue || recordData?.currencyValue,
          paymentMethodValue:
            cleanedValues.paymentMethodValue || recordData?.paymentMethodValue,
          paymentTermsValue:
            cleanedValues.paymentTermsValue || recordData?.paymentTermsValue,
          internationalTradeTermsValue:
            cleanedValues.internationalTradeTermsValue ||
            recordData?.internationalTradeTermsValue,
          shoppingName: cleanedValues.shoppingName || recordData?.shoppingName,
          accountGroupName:
            cleanedValues.accountGroupName || recordData?.accountGroupName,
          accountGroupValue:
            cleanedValues.accountGroupValue || recordData?.accountGroupValue,
          accountCode: cleanedValues.accountCode || recordData?.accountCode,
          oaAddress: cleanedValues.oaAddress || recordData?.oaAddress,
          oaAddressCode:
            cleanedValues.oaAddressCode || recordData?.oaAddressCode,
          piInvoiceParty:
            cleanedValues.piInvoiceParty || recordData?.piInvoiceParty,
          piInvoicePartyCode:
            cleanedValues.piInvoicePartyCode || recordData?.piInvoicePartyCode,
        };
        setStep2InitialValue(step2Data);

        // 保存第一步数据并切换到第二步
        setCurrentStep(FormStep.STEP2);
      }
    } catch (error) {}
  };

  /**
   * 处理第二步上一步按钮点击
   */
  const handleStep2Prev = () => {
    // 返回第一步时，恢复用户在第一步输入的数据
    if (step1Data) {
      // 使用 setTimeout 确保状态更新后再恢复表单数据
      setTimeout(() => {
        formRef.current?.setFieldsValue(step1Data);
      }, 0);
    }
    setCurrentStep(FormStep.STEP1);
  };

  /**
   * 处理第二步暂存按钮点击
   * 提交完整的两步表单数据（第一步 + 第二步），状态为暂存（flowStatus=0）
   */
  const handleStep2Save = async () => {
    try {
      // 获取第二步的表单数据
      const step2Values = await formRef.current?.validateFields();
      if (step2Values && step1Data) {
        // 转换第二步数据，将值转换为名称+值的对应形式
        const transformedStep2Data =
          transformStep2FormDataWithNames(step2Values);

        // 合并第一步和第二步的数据
        let completeFormData = {
          ...step1Data,
          ...transformedStep2Data,
        };

        // 转换营业有效期数据格式（确保businessValidity是字符串格式）
        completeFormData = transformBusinessValidityData(completeFormData);

        // 删除文件字段，只保留 fileIds
        const FILE_FIELD_NAMES = [
          "businessLicense",
          "bankDocs",
          "paymentAuthorizationLetter",
          "bankOpeningProof",
          "otherDocs",
        ];
        FILE_FIELD_NAMES.forEach((fieldName) => {
          delete completeFormData[fieldName];
        });

        onSubmit(completeFormData as SupplierAdmissionCompleteFormData, 0);
        handleCancel();
      } else {
      }
    } catch (error) {}
  };

  /**
   * 处理第二步提交按钮点击
   * 提交完整的两步表单数据（第一步 + 第二步），状态为提交（flowStatus=1）
   * 如果从工作台进入，则调用审批接口
   */
  const handleStep2Submit = async () => {
    try {
      // 获取第二步的表单数据
      const step2Values = await formRef.current?.validateFields();

      // 如果从工作台进入，调用审批接口
      if (isFromWorkbench && recordData?.taskId) {
        // 获取审核意见和意见说明
        const reviewOpinion = step2Values?.reviewOpinion;
        const reviewComments = step2Values?.reviewComments;

        if (!reviewOpinion) {
          message.error(t("请选择审核意见"));
          return;
        }

        if (!reviewComments) {
          message.error(t("请输入意见说明"));
          return;
        }

        // 构建审批参数
        const approvalData = {
          skipType: reviewOpinion === "agree" ? "PASS" : "",
          message: reviewComments,
          flowCode: "ZHUN_RU_SHEN_QING",
          variable: {
            businessId: recordData.id,
          },
        };

        try {
          const response = await passFlow(recordData.taskId, approvalData);

          if (response?.data?.code === 200) {
            message.success(
              reviewOpinion === "agree" ? t("审批通过成功") : t("驳回成功"),
            );
            handleCancel();
          } else {
            message.error(response?.data?.message || t("审批失败，请重试"));
          }
        } catch (error) {}
        return;
      }

      // 正常流程：需要 step1Data 和 step2Values
      if (step2Values && step1Data) {
        // 转换第二步数据，将值转换为名称+值的对应形式
        const transformedStep2Data =
          transformStep2FormDataWithNames(step2Values);

        // 合并第一步和第二步的数据
        let completeFormData = {
          ...step1Data,
          ...transformedStep2Data,
        };

        // 转换营业有效期数据格式（确保businessValidity是字符串格式）
        completeFormData = transformBusinessValidityData(completeFormData);

        // 删除文件字段，只保留 fileIds
        const FILE_FIELD_NAMES = [
          "businessLicense",
          "bankDocs",
          "paymentAuthorizationLetter",
          "bankOpeningProof",
          "otherDocs",
        ];
        FILE_FIELD_NAMES.forEach((fieldName) => {
          delete completeFormData[fieldName];
        });

        onSubmit(completeFormData as SupplierAdmissionCompleteFormData, 1);
        handleCancel();
      } else {
      }
    } catch (error) {}
  };

  const handleCancel = () => {
    formRef.current?.resetFields();
    setCurrentStep(FormStep.STEP1);
    setStep1Data(null);
    setErrorBankNumbers([]); // 清空错误的开户行行号
    onClose();
  };

  // 根据当前步骤选择不同的表单项
  const currentFormItems =
    currentStep === FormStep.STEP1 ? formItems : step2FormItems;

  // 根据模式和当前步骤选择不同的底部按钮
  // 只有在新增模式下才显示"暂存"按钮；编辑模式、申请变更模式和重新提交模式都不显示
  // insId 用于判断是否是新增申请：没有 insId = 未保存过 = 新增
  const showSaveButton =
    !isApplyChangeMode && !recordData?.insId && !isResubmitMode;

  // 从工作台进入时，第二步只显示提交按钮
  let modalFooter;
  if (isFromWorkbench && currentStep === FormStep.STEP2) {
    modalFooter = getStep2ModalFooter(
      handleStep2Prev,
      handleStep2Save,
      handleStep2Submit,
      false, // 不显示暂存按钮
      true, // isFromWorkbench = true
      t,
    );
  } else if (readOnly) {
    modalFooter =
      currentStep === FormStep.STEP1
        ? getReadOnlyStep1ModalFooter(handleStep1Next, handleCancel, t)
        : getReadOnlyStep2ModalFooter(handleStep2Prev, handleCancel, t);
  } else if (currentStep === FormStep.STEP1) {
    modalFooter = getModalFooter(handleStep1Next, t);
  } else {
    modalFooter = getStep2ModalFooter(
      handleStep2Prev,
      handleStep2Save,
      handleStep2Submit,
      showSaveButton,
      false, // isFromWorkbench = false
      t,
    );
  }

  return (
    <Modal
      title={modalTitle}
      open={visible}
      width="1300px"
      centered
      style={{ top: 10, paddingBottom: 20 }}
      classNames={{
        header: styles.modalHeader,
        body: styles.modalBody,
      }}
      footer={() => modalFooter}
      onCancel={handleCancel}
    >
      <div style={{ display: "flex", gap: "20px" }}>
        {/* 左侧表单区域 */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <DynamicForm
            ref={formRef}
            formItem={currentFormItems}
            formConfig={dynamicFormConfig}
            formValue={
              currentStep === FormStep.STEP1
                ? formInitialValue
                : step2InitialValue
            }
            readOnly={readOnly}
            isViewMode={readOnly}
          />
        </div>

        {/* 右侧流程动态区域 - 当只读模式或重新提交模式的第一步且有历史数据时显示 */}
        {(readOnly || (isResubmitMode && currentStep === FormStep.STEP1)) &&
          timelineData.length > 0 && (
            <div style={{ width: "320px", flexShrink: 0 }}>
              <div
                style={{
                  marginBottom: "12px",
                  fontWeight: 600,
                  fontSize: "14px",
                  color: "#1d2129",
                }}
              >
                {t("流程动态")}
              </div>
              <ProcessTimeline items={timelineData} />
            </div>
          )}
      </div>
    </Modal>
  );
};

export default AdmissionApplicationModal;
